const MIN_TERMINAL_DIMENSION = 1;
const MAX_TERMINAL_DIMENSION = 65535;

const inputEncoder = new TextEncoder();

/** cols/rows をサーバーが受け付ける整数範囲 (1..65535) に丸める。 */
function clampTerminalDimension(value: number): number {
	const truncated = Math.trunc(value);
	if (!Number.isFinite(truncated)) return MIN_TERMINAL_DIMENSION;
	return Math.min(MAX_TERMINAL_DIMENSION, Math.max(MIN_TERMINAL_DIMENSION, truncated));
}

/**
 * PTY 実行用 WebSocket の URL を組み立てる。API_URL と window.location を注入してテスト可能にする。
 * API_URL が空なら同一オリジン (location から ws/wss を導出)、絶対 URL なら http(s) を ws(s) へ置き換える。
 */
export function buildTerminalPtyWsUrl(
	environmentId: string,
	cols: number,
	rows: number,
	apiUrl: string,
	location: { protocol: string; host: string },
): string {
	const origin =
		apiUrl === ""
			? `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`
			: apiUrl.replace(/^http/, "ws");
	const clampedCols = clampTerminalDimension(cols);
	const clampedRows = clampTerminalDimension(rows);
	return `${origin}/api/environments/${encodeURIComponent(environmentId)}/terminal/ws?cols=${clampedCols}&rows=${clampedRows}`;
}

/** accept 直後に送る start ハンドシェイクを JSON テキストフレームとして組み立てる。 */
export function buildTerminalPtyStartMessage(
	command: string,
	chatId: string | null,
	terminalId: string,
): string {
	return JSON.stringify({ type: "start", command, chatId, terminalId });
}

/**
 * WebSocket クローズコードをユーザー向け日本語文言へ対応づける純関数。exit イベントを伴わない切断で使う。
 * 個別に扱うコードは 4001 (環境停止)、4003 (利用不可)、4007 (デバイスでエージェント環境を準備できていない)、
 * 4008 (デバイスのライセンスが支払い待ち)、1002/1009 (通信エラー) のみ。
 * 4002/4004 は廃番で、万一受信しても一般的な切断メッセージへフォールバックする。
 */
export function terminalPtyCloseMessage(code: number): string {
	switch (code) {
		case 4001:
			return "エージェントが起動していないため、コマンドを実行できませんでした。";
		case 4003:
			return "ターミナルを利用できません。しばらくしてからもう一度お試しください。";
		case 4007:
			return "デバイスでエージェント環境を準備できていないため、ターミナルを利用できません。しばらく待ってから開き直してください。";
		case 4008:
			return "このデバイスのライセンスの更新額をクレジットから引けないため、ターミナルを使えません。クレジットが追加されると使えるようになります。";
		case 1002:
		case 1009:
			return "通信エラーが発生したため、コマンドの実行を中断しました。";
		default:
			return "接続が切断されたため、コマンドの実行を中断しました。";
	}
}

/**
 * サーバーの出力フロー制御に応えるための ACK 会計。受信済みバイトのうち、xterm が実際に描画 (flush) した
 * 累積バイト数を単調増加で追跡し、送るべき ACK 値を返す。サーバーは未 ACK 1MiB で出力を止めるため、
 * flush と ACK を連動させることでブラウザの消費速度がそのままバックプレッシャーになる。
 */
export class TerminalPtyAckAccounting {
	private received = 0;
	private flushed = 0;
	private lastAcked = 0;

	noteReceived(byteLength: number): void {
		this.received += byteLength;
	}

	/** flush 完了分を加算し、進捗があれば新しい累積 ACK 値を返す。進捗なしは null。受信超過はバグとして例外。 */
	noteFlushed(byteLength: number): number | null {
		this.flushed += byteLength;
		if (this.flushed > this.received) {
			throw new Error("flush したバイト数が受信済みバイト数を超えています。");
		}
		if (this.flushed <= this.lastAcked) return null;
		this.lastAcked = this.flushed;
		return this.lastAcked;
	}
}

/** WebSocket の最小インターフェース。実装は window.WebSocket、テストではフェイクを注入する。 */
export interface TerminalPtySocket {
	onopen: (() => void) | null;
	onmessage: ((event: { data: ArrayBuffer | string }) => void) | null;
	onclose: ((event: { code: number }) => void) | null;
	send(data: Uint8Array | string): void;
	close(code?: number): void;
}

export type TerminalPtySocketFactory = (url: string) => TerminalPtySocket;

/** PTY 出力の書き出し先。xterm の write(data, callback) に対応する。 */
export interface TerminalPtySink {
	write(data: Uint8Array, onFlushed: () => void): void;
}

export interface TerminalPtySessionCallbacks {
	/** シェルが終了した (exit イベント + close 1000)。cwd は状態永続化の結果 (取得失敗時は null)。 */
	onExit(exitCode: number, cwd: string | null): void;
	/** exit を伴わない終了 (異常な close・通信エラー・不正応答)。message は必ずユーザーへ表示する。 */
	onEnded(message: string): void;
}

export interface TerminalPtySessionOptions {
	buildUrl: (cols: number, rows: number) => string;
	socketFactory: TerminalPtySocketFactory;
	/** onopen で resize より先に送る start ハンドシェイク。 */
	startMessage: string;
	sink: TerminalPtySink;
	callbacks: TerminalPtySessionCallbacks;
}

/**
 * 1コマンドの PTY 実行を担う状態機械。start フレームにコマンドが載るため自動再接続はしない
 * (再接続 = コマンド再実行になる)。どの close も終端で、onExit か onEnded のいずれか1回だけ呼ぶ。
 * UI に依存せず、注入した socketFactory / sink 越しにのみ副作用を起こすため node 環境で単体テストできる。
 */
export class TerminalPtySession {
	private readonly buildUrl: (cols: number, rows: number) => string;
	private readonly socketFactory: TerminalPtySocketFactory;
	private readonly startMessage: string;
	private readonly sink: TerminalPtySink;
	private readonly callbacks: TerminalPtySessionCallbacks;

	private socket: TerminalPtySocket | null = null;
	private connected = false;
	private disposed = false;
	private exit: { exitCode: number; cwd: string | null } | null = null;
	private lastCols = MIN_TERMINAL_DIMENSION;
	private lastRows = MIN_TERMINAL_DIMENSION;
	private readonly ack = new TerminalPtyAckAccounting();

	constructor(options: TerminalPtySessionOptions) {
		this.buildUrl = options.buildUrl;
		this.socketFactory = options.socketFactory;
		this.startMessage = options.startMessage;
		this.sink = options.sink;
		this.callbacks = options.callbacks;
	}

	connect(cols: number, rows: number): void {
		if (this.disposed || this.socket !== null) return;
		this.lastCols = cols;
		this.lastRows = rows;
		const socket = this.socketFactory(this.buildUrl(cols, rows));
		this.socket = socket;
		socket.onopen = () => {
			if (this.disposed || socket !== this.socket) return;
			this.connected = true;
			socket.send(this.startMessage);
			this.sendResize();
		};
		socket.onmessage = (event) => {
			if (this.disposed || socket !== this.socket) return;
			this.handleMessage(event, socket);
		};
		socket.onclose = (event) => {
			if (this.disposed || socket !== this.socket) return;
			this.connected = false;
			this.socket = null;
			this.handleClose(event.code);
		};
	}

	/** キー入力を UTF-8 のバイナリフレームで送る。未接続時は破棄する (open 前の打鍵をコマンドへ渡さない)。 */
	sendInput(data: string): void {
		if (!this.connected || this.socket === null || data.length === 0) return;
		this.socket.send(inputEncoder.encode(data));
	}

	/** 端末サイズを記憶し、接続中なら resize 制御メッセージを送る。 */
	resize(cols: number, rows: number): void {
		this.lastCols = cols;
		this.lastRows = rows;
		this.sendResize();
	}

	dispose(): void {
		if (this.disposed) return;
		this.disposed = true;
		this.connected = false;
		const socket = this.socket;
		this.socket = null;
		socket?.close(1000);
	}

	/** 回復不能なエラーで実行を中断する。dispose 済みなら何もしない。onEnded 経由で呼び出し元へ通知する。 */
	private fail(message: string): void {
		if (this.disposed) return;
		this.dispose();
		this.callbacks.onEnded(message);
	}

	private handleMessage(event: { data: ArrayBuffer | string }, socket: TerminalPtySocket): void {
		if (typeof event.data === "string") {
			let parsed: { type?: string; exitCode?: number; cwd?: unknown };
			try {
				parsed = JSON.parse(event.data) as { type?: string; exitCode?: number; cwd?: unknown };
			} catch (caught) {
				console.error("terminal pty message parse failed", caught);
				this.fail("サーバーからの応答を解釈できませんでした。コマンドの実行を中断しました。");
				return;
			}
			if (parsed.type === "exit" && typeof parsed.exitCode === "number") {
				// onExit は直後の close(1000) で発火する。
				this.exit = {
					exitCode: parsed.exitCode,
					cwd: typeof parsed.cwd === "string" ? parsed.cwd : null,
				};
			}
			return;
		}
		const bytes = new Uint8Array(event.data);
		if (bytes.byteLength === 0) return;
		this.ack.noteReceived(bytes.byteLength);
		this.sink.write(bytes, () => {
			if (this.disposed || socket !== this.socket) return; // 破棄済み・切断後の flush では ACK しない。
			try {
				const acknowledged = this.ack.noteFlushed(bytes.byteLength);
				if (acknowledged !== null)
					socket.send(JSON.stringify({ type: "ack", bytes: acknowledged }));
			} catch (caught) {
				console.error("terminal pty flush accounting failed", caught);
				this.fail("ターミナル出力の同期に失敗したため、コマンドの実行を中断しました。");
			}
		});
	}

	private handleClose(code: number): void {
		if (this.exit !== null) {
			this.callbacks.onExit(this.exit.exitCode, this.exit.cwd);
			return;
		}
		if (code === 1000) {
			this.callbacks.onEnded("コマンドの終了状態を確認できませんでした。");
			return;
		}
		this.callbacks.onEnded(terminalPtyCloseMessage(code));
	}

	private sendResize(): void {
		if (!this.connected || this.socket === null) return;
		this.socket.send(
			JSON.stringify({
				type: "resize",
				cols: clampTerminalDimension(this.lastCols),
				rows: clampTerminalDimension(this.lastRows),
			}),
		);
	}
}
